import React, { useEffect, useRef, useState } from 'react';
import { initNaverMaps } from '../utils/naverMapsLoader';
import { getCurrentLanguage, LANGUAGE_CHANGE_EVENT } from '../config/languageConfig';

interface NaverMapProps {
  initialCenter?: { lat: number; lng: number };
  onLocationSelect?: (location: { lat: number; lng: number }) => void;
  width?: string | number;
  height?: string | number;
  zoom?: number;
  draggable?: boolean;
  markerDraggable?: boolean;
}

const NaverMap: React.FC<NaverMapProps> = React.memo(({
  initialCenter = { lat: 37.5665, lng: 126.9780 }, // 서울시청 기본 좌표
  onLocationSelect,
  width = '100%',
  height = '300px',
  zoom = 15,
  draggable = true,
  markerDraggable = true
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false);
  const [currentLanguage, setCurrentLanguage] = useState<string>(getCurrentLanguage());
  const clickListenerRef = useRef<any>(null);
  const markerDragListenerRef = useRef<any>(null);

  // 언어 변경 감지
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = getCurrentLanguage();
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
        // 안전하게 상태 초기화
        setIsScriptLoaded(false);
        setMapInstance(null);
        setMarker(null);
        setMapError(null);
      }
    };
    
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, [currentLanguage]);

  // 네이버 맵 스크립트 로드
  useEffect(() => {
    
    const loadScript = async () => {
      try {
        setMapError(null); // 에러 상태 초기화
        const loaded = await initNaverMaps();
        
        if (loaded) {
          // 언어가 변경되지 않았을 때만 로드 완료 처리
          if (getCurrentLanguage() === currentLanguage) {
            setIsScriptLoaded(true);
          } 
        } else {
          setMapError('네이버 맵스 SDK를 로드하지 못했습니다.');
        }
      } catch (error) {
        console.error('❌ NaverMap: Script load error:', error);
        setMapError('네이버 맵스 SDK를 로드하지 못했습니다.');
      }
    };
    
    // 언어가 설정되어 있을 때만 로드
    if (currentLanguage) {
      loadScript();
    } 
  }, [currentLanguage]);

  // 맵 초기화 - 스크립트가 로드된 후에만 실행
  useEffect(() => {
    if (!isScriptLoaded || !mapRef.current) {
      return;
    }
    
    let map: any = null;
    let mapMarker: any = null;
    
    // DOM이 완전히 준비될 때까지 지연
    const initializeMap = () => {
      
      // DOM 요소 크기 확인
      const rect = mapRef.current?.getBoundingClientRect();
      
      if (!rect || rect.width === 0 || rect.height === 0) {
        setTimeout(initializeMap, 100);
        return;
      }
      
      try {
        // 지도 생성
        map = new window.naver.maps.Map(mapRef.current, {
          center: new window.naver.maps.LatLng(initialCenter.lat, initialCenter.lng),
          zoom: zoom,
          draggable: draggable,
          zoomControl: true,
          zoomControlOptions: {
            position: window.naver.maps.Position.TOP_RIGHT
          }
        });
        
        
        // 마커 생성
        mapMarker = new window.naver.maps.Marker({
          position: new window.naver.maps.LatLng(initialCenter.lat, initialCenter.lng),
          map: map,
          draggable: markerDraggable
        });
        
        // 마커 드래그 이벤트
        if (markerDraggable && onLocationSelect) {
          markerDragListenerRef.current = window.naver.maps.Event.addListener(mapMarker, 'dragend', () => {
            try {
              const position = mapMarker.getPosition();
              if (position) {
                const location = {
                  lat: position.lat(),
                  lng: position.lng()
                };
                onLocationSelect(location);
              }
            } catch (error) {
              console.error('마커 드래그 이벤트 처리 중 오류:', error);
            }
          });
        }
        
        // 지도 클릭 이벤트 - markerDraggable이 true일 때만 등록
        if (markerDraggable && onLocationSelect) {
          clickListenerRef.current = window.naver.maps.Event.addListener(map, 'click', (e: any) => {
            try {
              if (!mapMarker) return;
              
              const clickedPos = e.coord;
              
              mapMarker.setPosition(clickedPos);
              
              if (onLocationSelect) {
                onLocationSelect({
                  lat: clickedPos.lat(),
                  lng: clickedPos.lng()
                });
              }
            } catch (error) {
              console.error('지도 클릭 이벤트 처리 중 오류:', error);
            }
          });
        }
        
        setMapInstance(map);
        setMarker(mapMarker);
        
        // 맵 초기화 완료 후 강제 리사이즈 (지도 표시 문제 해결)
        setTimeout(() => {
          try {
            if (map && typeof map.refresh === 'function') {
              map.refresh();
            }
            // 윈도우 리사이즈 이벤트 발생시켜 지도 재계산
            window.dispatchEvent(new Event('resize'));
          } catch (error) {
            console.warn('맵 리프레시 중 오류:', error);
          }
        }, 100);
        
      } catch (error) {
        console.error('지도 초기화 중 오류 발생:', error);
        setMapError('지도를 초기화하는 중 오류가 발생했습니다.');
      }
    };
    
    // 초기화 지연 실행
    setTimeout(initializeMap, 100);
    
    // 컴포넌트 언마운트 시 정리 작업
    return () => {
      // 정리 작업을 지연시켜 React의 DOM 업데이트와 충돌 방지
      setTimeout(() => {
        try {
          // 이벤트 리스너 제거
          if (clickListenerRef.current && window.naver?.maps?.Event) {
            try {
              window.naver.maps.Event.removeListener(clickListenerRef.current);
            } catch (error) {
              console.warn('클릭 이벤트 리스너 제거 중 오류:', error);
            }
            clickListenerRef.current = null;
          }
          
          if (markerDragListenerRef.current && window.naver?.maps?.Event) {
            try {
              window.naver.maps.Event.removeListener(markerDragListenerRef.current);
            } catch (error) {
              console.warn('마커 드래그 이벤트 리스너 제거 중 오류:', error);
            }
            markerDragListenerRef.current = null;
          }
          
          // 마커 제거
          if (mapMarker) {
            try {
              mapMarker.setMap(null);
            } catch (error) {
              console.warn('마커 제거 중 오류:', error);
            }
          }
          
          // 맵 제거
          if (map && window.naver?.maps) {
            try {
              // 맵에 연결된 모든 이벤트 리스너 제거 시도
              if (window.naver.maps.Event && typeof window.naver.maps.Event.clearListeners === 'function') {
                window.naver.maps.Event.clearListeners(map);
              }
              
              // destroy 호출 전 상태 검증
              if (typeof map.destroy === 'function') {
                map.destroy();
              }
            } catch (error) {
              console.warn('지도 정리 중 오류 발생:', error);
            }
          }
        } catch (e) {
          console.warn('정리 작업 중 오류 발생:', e);
        }
      }, 0);
    };
  }, [zoom, draggable, markerDraggable, onLocationSelect, isScriptLoaded]);

  // initialCenter가 변경된 경우 지도와 마커 위치 업데이트
  useEffect(() => {
    if (!isScriptLoaded) return;
    
    if (mapInstance && marker && window.naver) {
      try {
        const position = new window.naver.maps.LatLng(initialCenter.lat, initialCenter.lng);
        mapInstance.setCenter(position);
        marker.setPosition(position);
      } catch (error) {
        console.error('지도 중심 업데이트 중 오류:', error);
      }
    }
  }, [initialCenter, mapInstance, marker, isScriptLoaded]);

  // markerDraggable 속성이 변경된 경우 마커의 draggable 속성 및 이벤트 리스너 업데이트
  useEffect(() => {
    if (!isScriptLoaded || !marker || !mapInstance || !window.naver?.maps) return;
    
    try {
      // 마커의 draggable 속성 업데이트
      if (typeof marker.setDraggable === 'function') {
        marker.setDraggable(markerDraggable);
      }
      
      // 기존 드래그 이벤트 리스너 제거
      if (markerDragListenerRef.current && window.naver?.maps?.Event) {
        try {
          window.naver.maps.Event.removeListener(markerDragListenerRef.current);
          markerDragListenerRef.current = null;
        } catch (error) {
          console.warn('기존 마커 드래그 이벤트 리스너 제거 중 오류:', error);
        }
      }
      
      // 기존 클릭 이벤트 리스너 제거
      if (clickListenerRef.current && window.naver?.maps?.Event) {
        try {
          window.naver.maps.Event.removeListener(clickListenerRef.current);
          clickListenerRef.current = null;
        } catch (error) {
          console.warn('기존 지도 클릭 이벤트 리스너 제거 중 오류:', error);
        }
      }
      
      // markerDraggable이 true이고 onLocationSelect가 있는 경우에만 이벤트 리스너들 등록
      if (markerDraggable && onLocationSelect && window.naver?.maps?.Event) {
        // 마커 드래그 이벤트 리스너 등록
        try {
          markerDragListenerRef.current = window.naver.maps.Event.addListener(marker, 'dragend', () => {
            try {
              const position = marker.getPosition();
              if (position && typeof position.lat === 'function' && typeof position.lng === 'function') {
                const location = {
                  lat: position.lat(),
                  lng: position.lng()
                };
                onLocationSelect(location);
              }
            } catch (error) {
              console.warn('마커 드래그 이벤트 처리 중 오류:', error);
            }
          });
        } catch (error) {
          console.warn('마커 드래그 이벤트 리스너 등록 중 오류:', error);
        }
        
        // 지도 클릭 이벤트 리스너 등록
        try {
          clickListenerRef.current = window.naver.maps.Event.addListener(mapInstance, 'click', (e: any) => {
            try {
              if (!marker || !e?.coord) return;
              
              const clickedPos = e.coord;
              
              if (typeof marker.setPosition === 'function' && 
                  typeof clickedPos.lat === 'function' && 
                  typeof clickedPos.lng === 'function') {
                marker.setPosition(clickedPos);
                
                onLocationSelect({
                  lat: clickedPos.lat(),
                  lng: clickedPos.lng()
                });
              }
            } catch (error) {
              console.warn('지도 클릭 이벤트 처리 중 오류:', error);
            }
          });
        } catch (error) {
          console.warn('지도 클릭 이벤트 리스너 등록 중 오류:', error);
        }
      }
    } catch (error) {
      console.warn('마커 draggable 속성 업데이트 중 오류:', error);
    }
  }, [markerDraggable, onLocationSelect, marker, isScriptLoaded, mapInstance]);

  // 에러 상태 또는 로딩 상태
  if (mapError) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          color: '#e53935'
        }}
      >
        <p>{mapError}</p>
      </div>
    );
  }
  
  if (!isScriptLoaded) {
    return (
      <div
        style={{
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
        <p className="text-gray-600">지도를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div
      ref={mapRef}
      style={{ 
        width, 
        height, 
        borderRadius: '8px', 
        overflow: 'hidden',
        minHeight: '300px',
        position: 'relative',
        display: 'block'
      }}
      className="relative border border-gray-300 shadow-sm"
    >
      <div className="absolute bottom-2 right-2 bg-white py-1 px-2 rounded text-xs text-gray-500 shadow-sm z-10">
        © Naver Maps
      </div>
    </div>
  );
});

export default NaverMap;
